// ============================================================
// ObjectRing - an Object's ground on the display (2026-10-26, board 5 of the Cover & Objects canvas).
//
// Its ring IS its health gauge: a dim track, and over it the arc its Wounds leave, in the health ramp's colour,
// stepped by band when this viewer may not read the numbers (so the arc leaks none). Counting as terrain, the ring
// runs round its box's footprint instead (board 6), from the first corner. Destroyed, the ring goes dashed
// and the shards lie in it until the GM removes the token; they land with a short shatter, held still with motion off.
// Same test id as every figure's ground, so the specs that find a foot find this one.
// ============================================================
import clsx from '../../utils/clsx';
import { gaugeArc, outlineGauge, shardsAt, type ObjectState } from '../../engine/plate/objectPlate';
import { closedPath } from '../../engine/display/svgPath';
import { healthColor, healthGauge } from '../../engine/healthRamp';
import styles from '../../styles/ObjectMark.module.css';
import type { MarkerGeometry, ScreenXY } from './markerGeometry';

/** What the marker knows of an Object's health (markerDecorations `objectsFor`). */
export interface ObjectHealth {
  state: ObjectState;
  shown: number;
  total: number;
}

interface Props {
  id: string;
  foot: ScreenXY;
  g: MarkerGeometry;
  health: ObjectHealth;
  showNumbers: boolean;
  /** The role stroke (blue you, red your target): the track wears it, as every figure's ring does. */
  colour: string;
  /** This screen plays no motion: the shards lie where they fell. */
  still: boolean;
  /** A terrain Object's box footprint on the screen: the ring runs round it instead of the round ring. */
  outline?: ScreenXY[] | null;
}

/** The track and the gauge: round the box's footprint, or the round ring. */
function trackAndGauge(foot: ScreenXY, g: MarkerGeometry, fill: number, outline: ScreenXY[] | null | undefined) {
  if (!outline?.length) return { track: null, gauge: gaugeArc(foot.x, foot.y, g.rx, g.ry, fill) };
  const run = outlineGauge(outline, fill);
  const gauge = run.length ? `M${run.map((p) => `${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' L')}` : '';
  return { track: closedPath(outline), gauge };
}

export default function ObjectRing({ id, foot, g, health, showNumbers, colour, still, outline }: Props) {
  const destroyed = health.state === 'destroyed';
  const fill = healthGauge(health.shown, health.total, showNumbers);
  const { track, gauge } = trackAndGauge(foot, g, fill, outline);
  const trackClass = clsx(styles.track, destroyed && styles.trackDestroyed);
  return (
    <g
      data-testid={`token-foot-${id}`}
      data-object-state={health.state}
      data-fill={fill}
      data-outline={track ? 'box' : 'ring'}
    >
      {track ? (
        <path className={trackClass} d={track} stroke={colour} />
      ) : (
        <ellipse className={trackClass} cx={foot.x} cy={foot.y} rx={g.rx} ry={g.ry} stroke={colour} />
      )}
      {!destroyed && gauge && (
        <path
          data-testid={`object-gauge-${id}`}
          className={styles.gauge}
          d={gauge}
          stroke={healthColor(health.shown, health.total)}
        />
      )}
      {destroyed && (
        <g data-testid={`object-shards-${id}`} className={clsx(styles.shards, still && styles.still)}>
          {shardsAt(foot.x, foot.y, g.rx * 1.6).map((points) => (
            <polygon key={points} points={points} />
          ))}
        </g>
      )}
    </g>
  );
}
